import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import TimerIcon from "@mui/icons-material/Timer";
import RefreshIcon from "@mui/icons-material/Refresh";
import { hubApi } from "../services/hubApi";

const statCards = [
  {
    key: "totalOutbound",
    label: "Realizadas",
    icon: PhoneForwardedIcon,
    color: "#25D366",
    bg: "rgba(37, 211, 102, 0.1)",
  },
  {
    key: "totalInbound",
    label: "Recebidas",
    icon: PhoneCallbackIcon,
    color: "#3B82F6",
    bg: "rgba(59, 130, 246, 0.1)",
  },
  {
    key: "totalMissed",
    label: "Nao Atendidas",
    icon: PhoneMissedIcon,
    color: "#EF4444",
    bg: "rgba(239, 68, 68, 0.1)",
  },
  {
    key: "avgDuration",
    label: "Duracao Media",
    icon: TimerIcon,
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.1)",
    format: "duration",
  },
];

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0s";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export default function VoIPDashboard() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await hubApi.voipGetDashboard(period);
      setStats(data.stats || {});
    } catch (err) {
      console.error("Error loading VoIP dashboard:", err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6" }}>
          Dashboard VoIP
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e, v) => v && setPeriod(v)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                color: "#9CA3AF",
                borderColor: "rgba(255,255,255,0.1)",
                fontSize: "0.75rem",
                px: 1.5,
                py: 0.5,
                "&.Mui-selected": {
                  bgcolor: "rgba(139, 92, 246, 0.15)",
                  color: "#8B5CF6",
                  borderColor: "rgba(139, 92, 246, 0.3)",
                },
              },
            }}
          >
            <ToggleButton value="today">Hoje</ToggleButton>
            <ToggleButton value="7d">7 dias</ToggleButton>
            <ToggleButton value="30d">30 dias</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={loadStats} sx={{ color: "#6B7280" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Cards */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: "#8B5CF6" }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {statCards.map((card) => {
            const Icon = card.icon;
            const value =
              card.format === "duration"
                ? formatDuration(stats?.[card.key] || 0)
                : stats?.[card.key] || 0;

            return (
              <Grid item xs={6} md={3} key={card.key}>
                <Card
                  sx={{
                    bgcolor: "#1E293B",
                    border: "1px solid rgba(255,255,255,0.04)",
                    borderRadius: "14px",
                  }}
                >
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: card.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon sx={{ fontSize: 22, color: card.color }} />
                      </Box>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#F3F4F6",
                        fontSize: "1.75rem",
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#9CA3AF", fontSize: "0.75rem" }}
                    >
                      {card.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
